import { lazy, Suspense, useEffect, useRef } from "react";
import type React from "react";
import { ClerkProvider, SignIn, SignUp, Show, useClerk } from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { shadcn } from "@clerk/themes";
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthLayout, CampaignLayout, MainLayout } from "@/layouts";
import RouteMetadata from "@/components/RouteMetadata";
import MarketingPrivacy from "@/components/MarketingPrivacy";
import { publicHref } from "@/lib/routeMetadata";
import "@/auth.css";

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const CustomDirectMail = lazy(() => import("@/pages/CustomDirectMail"));
const Industries = lazy(() => import("@/pages/Industries"));
const HvacPage = lazy(() => import("@/pages/HvacPage"));
const IndustryV2Page = lazy(() => import("@/pages/IndustryV2Page"));
const PrintShop = lazy(() => import("@/pages/PrintShop"));
const Postcards = lazy(() => import("@/pages/Postcards"));
const Sheets = lazy(() => import("@/pages/Sheets"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Order = lazy(() => import("@/pages/Order"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const OrderDetail = lazy(() => import("@/pages/OrderDetail"));
const Cart = lazy(() => import("@/pages/Cart"));
const Quote = lazy(() => import("@/pages/Quote"));
const BookDemo = lazy(() => import("@/pages/BookDemo"));
const DemoLanding = lazy(() => import("@/pages/DemoLanding"));
const CalculatorPage = lazy(() => import("@/pages/CalculatorPage"));
const MapIntel = lazy(() => import("@/pages/MapIntel"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const Terms = lazy(() => import("@/pages/Terms"));
const Legal = lazy(() => import("@/pages/Legal"));
const CausalAnalytics = lazy(() => import("@/pages/CausalAnalytics"));
const CheckoutLinkGate = lazy(() => import("@/pages/CheckoutLinkGate"));
const DirectMail = lazy(() => import("@/pages/services/DirectMail"));
const WebsiteDevelopment = lazy(() => import("@/pages/services/WebsiteDevelopment"));
const Seo = lazy(() => import("@/pages/services/Seo"));
const GoogleMetaAds = lazy(() => import("@/pages/services/GoogleMetaAds"));
const SolterraSolutions = lazy(() => import("@/pages/proposals/SolterraSolutions"));
const NewDayConstruction = lazy(() => import("@/pages/proposals/NewDayConstruction"));
// Development-only template review; tree-shaken from production builds.
const BlogPreviewIndex = import.meta.env.DEV ? lazy(() => import("@/blog-preview/BlogPreviewPage").then((m) => ({ default: m.PreviewIndex }))) : null;
const BlogPreviewArticle = import.meta.env.DEV ? lazy(() => import("@/blog-preview/BlogPreviewPage").then((m) => ({ default: m.PreviewArticle }))) : null;
const BlogAdmin = lazy(() => import("@/pages/BlogAdmin"));
const NotFound = lazy(() => import("@/pages/not-found"));

const queryClient = new QueryClient();

const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const clerkProxyUrl = import.meta.env.PROD
  ? import.meta.env.VITE_CLERK_PROXY_URL
  : undefined;

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

const clerkAppearance = {
  theme: shadcn,
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.webp`,
  },
  variables: {
    colorPrimary: "#FF6B00",
    colorForeground: "#F2EFE9",
    colorMutedForeground: "#a89e95",
    colorDanger: "#ef4444",
    colorBackground: "#222222",
    colorInput: "#2d2d2d",
    colorInputForeground: "#F2EFE9",
    colorNeutral: "#333333",
    fontFamily: "Inter, sans-serif",
    borderRadius: "0.5rem",
  },
  elements: {
    rootBox: "w-full min-w-0 max-w-full flex justify-center",
    cardBox: "bg-[#222222] rounded-xl w-full min-w-0 max-w-full shadow-2xl border border-[#333]",
    card: "gw-auth-card min-w-0 max-w-full !shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "gw-auth-footer !shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-[#F2EFE9] font-bold text-2xl",
    headerSubtitle: "text-[#a89e95] [overflow-wrap:anywhere]",
    socialButtonsBlockButtonText: "text-[#F2EFE9]",
    formFieldLabel: "text-[#F2EFE9] font-medium",
    footerActionLink: "text-[#FF6B00] hover:text-[#e8590c]",
    footerActionText: "text-[#a89e95]",
    dividerText: "text-[#a89e95]",
    identityPreviewEditButton: "text-[#FF6B00]",
    identityPreviewText: "[overflow-wrap:anywhere]",
    formFieldErrorText: "[overflow-wrap:anywhere]",
    formFieldSuccessText: "text-green-400",
    alertText: "text-[#F2EFE9] [overflow-wrap:anywhere]",
    logoBox: "mb-2",
    logoImage: "!h-12",
    socialButtonsBlockButton: {
      backgroundColor: "#2d2d2d",
      border: "1px solid #333",
      color: "#F2EFE9",
      minHeight: "52px",
      padding: "0 16px",
      fontSize: "16px",
      borderRadius: "0.5rem",
    },
    formButtonPrimary: {
      backgroundColor: "#FF6B00",
      color: "#1A1A1A",
      fontWeight: "700",
      minHeight: "52px",
      fontSize: "16px",
      borderRadius: "0.5rem",
    },
    formFieldInput: {
      backgroundColor: "#2d2d2d",
      border: "1px solid #444",
      color: "#F2EFE9",
      minHeight: "44px",
      padding: "10px 14px",
      fontSize: "16px",
      borderRadius: "0.5rem",
    },
    otpCodeFieldInput: {
      backgroundColor: "#2d2d2d",
      border: "1px solid #444",
      color: "#F2EFE9",
      minHeight: "52px",
      fontSize: "20px",
      borderRadius: "0.5rem",
      minWidth: "0",
      width: "min(2.25rem, 11vw)",
    },
    otpCodeFieldInputs: "gap-[clamp(.25rem,1.5vw,.5rem)]",
    footerAction: "bg-transparent flex-wrap",
    dividerLine: "bg-[#333]",
    alert: "bg-[#2d2d2d] border-[#444]",
    formFieldRow: "gap-2 min-w-0 max-w-full",
    formField: "min-w-0 max-w-full",
    form: "min-w-0 max-w-full",
    main: "gap-4 min-w-0 max-w-full",
  },
};

function AuthPage({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <main className="marketing-auth" aria-label="Account access">
      {/* Keep authentication isolated: do not mount the marketing homepage behind Clerk. */}
      <div style={{ position: "fixed", inset: 0, background: "radial-gradient(circle at top, #3a302a 0%, #1a1a1a 52%)", pointerEvents: "none" }} />
      {/* Normal document flow keeps tall forms and the mobile keyboard scrollable. */}
      <div className="marketing-auth-stage">
        <div className="w-full min-w-0 max-w-[520px]">
          <button
            onClick={onClose}
            aria-label="Go back"
            className="mb-3 flex min-h-11 items-center gap-2 text-sm text-[#c0b8b0] hover:text-[#F2EFE9] transition-colors"
          >
            ← Back
          </button>
          {children}
          <button type="button" className="mx-auto mt-3 block min-h-11 px-3 text-sm text-[#c0b8b0] underline underline-offset-4 hover:text-white"
            onClick={() => window.dispatchEvent(new Event("gorilla-privacy-open"))}>
            Privacy choices
          </button>
        </div>
      </div>
    </main>
  );
}

function SignInPage() {
  const [, setLocation] = useLocation();
  return (
    <AuthPage onClose={() => setLocation("/")}>
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        fallbackRedirectUrl="/"
        appearance={clerkAppearance}
      />
    </AuthPage>
  );
}

function SignUpPage() {
  const [, setLocation] = useLocation();
  return (
    <AuthPage onClose={() => setLocation("/")}>
      <SignUp
        routing="path"
        path={`${basePath}/sign-up`}
        signInUrl={`${basePath}/sign-in`}
        fallbackRedirectUrl="/"
        appearance={clerkAppearance}
      />
    </AuthPage>
  );
}

function HomeRedirect() {
  return <Home />;
}

/** Redirects to /sign-in while preserving the current path as redirect_url so Clerk sends the user back after they authenticate. */
function SignInRedirect() {
  const dest = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;
  return <Redirect to={dest} />;
}

function DashboardGuard() {
  return (
    <>
      <Show when="signed-in">
        <Dashboard />
      </Show>
      <Show when="signed-out">
        <SignInRedirect />
      </Show>
    </>
  );
}

function OrdersGuard() {
  return (
    <>
      <Show when="signed-in">
        <OrdersPage />
      </Show>
      <Show when="signed-out">
        <SignInRedirect />
      </Show>
    </>
  );
}

function OrderDetailGuard({ params }: { params: { id: string } }) {
  return (
    <>
      <Show when="signed-in">
        <OrderDetail id={parseInt(params.id)} />
      </Show>
      <Show when="signed-out">
        <SignInRedirect />
      </Show>
    </>
  );
}

function CartGuard() {
  return (
    <>
      <Show when="signed-in">
        <Cart />
      </Show>
      <Show when="signed-out">
        <SignInRedirect />
      </Show>
    </>
  );
}

function OrderGuard() {
  return (
    <>
      <Show when="signed-in">
        <Order />
      </Show>
      <Show when="signed-out">
        <SignInRedirect />
      </Show>
    </>
  );
}

function BlogAdminGuard() {
  return (
    <>
      <Show when="signed-in">
        <BlogAdmin />
      </Show>
      <Show when="signed-out">
        <SignInRedirect />
      </Show>
    </>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

export function AppRoutes() {
  return (
    <Switch>
      {/* Auth routes are structurally isolated — AuthLayout never renders Header/Footer/FloatingCTABanner */}
      <Route path="/sign-in/*?">
        <AuthLayout>
          <SignInPage />
        </AuthLayout>
      </Route>
      <Route path="/sign-up/*?">
        <AuthLayout>
          <SignUpPage />
        </AuthLayout>
      </Route>

      {/* Standalone proposals — no Header/Footer, no nav */}
      {/* NOTE: /proposals index has moved to /app/proposals (agency_admin only) */}
      <Route path="/proposals/solterrasolutions48" component={SolterraSolutions} />
      <Route path="/proposals/newdayconstruction" component={NewDayConstruction} />

      <Route path="/demo">
        <CampaignLayout><DemoLanding key="generic" /></CampaignLayout>
      </Route>
      <Route path="/demo/first-mail-drop-offer">
        <CampaignLayout><DemoLanding key="offer" offer /></CampaignLayout>
      </Route>
      <Route path="/demo/industries/:slug">{params =>
        <CampaignLayout><DemoLanding key={params.slug} slug={params.slug} /></CampaignLayout>
      }</Route>

      {/* All other routes share MainLayout */}
      <Route>
        <MainLayout>
          <Switch>
            <Route path="/" component={HomeRedirect} />
            {BlogPreviewIndex && <Route path="/__blog-preview" component={BlogPreviewIndex} />}
            {BlogPreviewArticle && <Route path="/__blog-preview/article" component={BlogPreviewArticle} />}
            <Route path="/admin/blog" component={BlogAdminGuard} />
            <Route path="/about" component={About} />
            <Route path="/contact" component={Contact} />
            <Route path="/custom-direct-mail" component={CustomDirectMail} />
            <Route path="/total-neighborhood-mail"><Redirect to="/custom-direct-mail" replace /></Route>
            <Route path="/industries" component={Industries} />
            <Route path="/industries/hvac" component={HvacPage} />
            <Route path="/industries/:slug">{(params) => <IndustryV2Page slug={params.slug} />}</Route>
            <Route path="/print-shop" component={PrintShop} />
            <Route path="/print-shop/postcards" component={Postcards} />
            <Route path="/print-shop/sheets" component={Sheets} />
            <Route path="/pricing" component={Pricing} />
            <Route path="/integrations" component={Integrations} />
            <Route path="/order" component={OrderGuard} />
            <Route path="/dashboard" component={DashboardGuard} />
            <Route path="/orders" component={OrdersGuard} />
            <Route path="/orders/:id">{(params) => <OrderDetailGuard params={params} />}</Route>
            <Route path="/cart" component={CartGuard} />
            <Route path="/quote" component={Quote} />
            <Route path="/book-demo" component={BookDemo} />
            <Route path="/causal-analytics" component={CausalAnalytics} />
            <Route path="/services/direct-mail" component={DirectMail} />
            <Route path="/services/website-development" component={WebsiteDevelopment} />
            <Route path="/services/seo" component={Seo} />
            <Route path="/services/google-meta-ads" component={GoogleMetaAds} />
            <Route path="/calculator" component={CalculatorPage} />
            <Route path="/map-intel" component={MapIntel} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/data-deletion" component={DataDeletion} />
            <Route path="/terms" component={Terms} />
            <Route path="/legal" component={Legal} />
            <Route path="/checkout/:token" component={CheckoutLinkGate} />
            <Route component={NotFound} />
          </Switch>
        </MainLayout>
      </Route>
    </Switch>
  );
}

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center" role="status" aria-live="polite">
      <span className="text-sm text-muted-foreground">Loading page…</span>
    </div>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: { start: { title: "Welcome Back", subtitle: "Sign in to your Gorilla Works account" } },
        signUp: { start: { title: "Get Started", subtitle: "Create your Gorilla Works account" } },
      }}
      routerPush={(to) => {
        // If Clerk redirects to the app-dashboard (/app/*), use a hard navigation
        // so wouter doesn't intercept it as an internal SPA route.
        if (to.startsWith("/app")) { window.location.href = to; return; }
        setLocation(stripBase(to));
      }}
      routerReplace={(to) => {
        if (to.startsWith("/app")) { window.location.replace(to); return; }
        setLocation(stripBase(to), { replace: true });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ClerkQueryClientCacheInvalidator />
          <Toaster />
          <RouteMetadata />
          <Suspense fallback={<RouteFallback />}>
            <AppRoutes />
          </Suspense>
          <MarketingPrivacy />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath} hrefs={publicHref}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
